import { simplePercentageFilter } from "app/filters/ngFilters";

import type { IFoldersItem, IFoldersAPIItem, IFoldersAPIResponse } from "../../../types";

const normalizeSearchTerm = (searchTerm?: string): string => {
    return searchTerm?.trim().toLowerCase() || "";
};

const matchesSearch = (item: IFoldersItem, searchLower: string): boolean => {
    return item.folder.toLowerCase().includes(searchLower) || item.specificFolder.toLowerCase().includes(searchLower);
};

const checkTrendingThreshold = (item: IFoldersItem): boolean => {
    const { lastMonthShare, prevMonthShare } = item;

    // If no current month data, don't show
    if (lastMonthShare === undefined) {
        return false;
    }

    // If no previous month data, consider it as "new" and show
    if (prevMonthShare === undefined) {
        return true;
    }

    // Calculate the change percentage
    const change = lastMonthShare - prevMonthShare;

    // Use the same formatting logic as PercentPointCell to check if it would display as 0.00
    const precision = 2;
    const filteredValue = simplePercentageFilter()(Math.abs(change), precision);

    // Show only if the formatted value is not 0 (positive change)
    // @ts-expect-error FIXME: Operator '>' cannot be applied to types 'string | number' and 'number'
    return filteredValue > 0;
};

const hasAnyTrendingDescendant = (item: IFoldersItem): boolean => {
    // For items with children: parent must meet threshold to be shown
    if (item.children?.length) {
        return checkTrendingThreshold(item);
    }

    // For leaf items: must meet threshold to be shown
    return checkTrendingThreshold(item);
};

const transformApiItem = (item: IFoldersAPIItem, parent?: IFoldersItem): IFoldersItem => ({
    folder: item.Folder,
    specificFolder: item.SpecificFolder,
    share: item.Share,
    lastMonthShare: item.LastMonthShare,
    prevMonthShare: item.PrevMonthShare,
    key: item.Folder,
    parent,
});

const transformWithSearch = (
    items: IFoldersAPIItem[] = [],
    searchTerm: string = "",
    parent?: IFoldersItem,
    isTrending?: boolean,
): IFoldersItem[] => {
    if (!items?.length) {
        return [];
    }

    const searchLower = normalizeSearchTerm(searchTerm);
    const hasSearch = searchLower.length > 0;
    const results: IFoldersItem[] = [];

    for (const item of items) {
        const processedItem = transformApiItem(item, parent);

        if (item.Children) {
            processedItem.children = transformWithSearch(item.Children, searchTerm, processedItem, isTrending);
        }

        // Check if item meets trending threshold
        const meetsTrendingThreshold = isTrending ? hasAnyTrendingDescendant(processedItem) : true;

        if (hasSearch) {
            const itemMatches = matchesSearch(processedItem, searchLower);
            const hasMatchingChild = processedItem.children?.length > 0;

            if ((itemMatches || hasMatchingChild) && meetsTrendingThreshold) {
                processedItem.shouldExpand = hasMatchingChild && !itemMatches;
                results.push(processedItem);
            }
        } else if (meetsTrendingThreshold) {
            results.push(processedItem);
        }
    }

    return results;
};

export const searchNestedData = (data: IFoldersItem[], searchTerm: string): IFoldersItem[] => {
    const searchLower = normalizeSearchTerm(searchTerm);
    if (!searchLower) {
        return data;
    }

    const results: IFoldersItem[] = [];

    for (const item of data) {
        const processedChildren = item.children ? searchNestedData(item.children, searchTerm) : undefined;
        const hasMatchingChild = processedChildren?.length > 0;
        const itemMatches = matchesSearch(item, searchLower);

        if (itemMatches || hasMatchingChild) {
            results.push({
                ...item,
                children: hasMatchingChild ? processedChildren : undefined,
                shouldExpand: hasMatchingChild && !itemMatches,
            });
        }
    }

    return results;
};

const getAllChildKeys = (item: IFoldersItem): string[] => {
    if (!item.children?.length) {
        return [];
    }

    const childKeys: string[] = [];
    for (const child of item.children) {
        childKeys.push(child.key);
        childKeys.push(...getAllChildKeys(child));
    }
    return childKeys;
};

const findItemByKey = (data: IFoldersItem[], key: string): IFoldersItem | null => {
    for (const item of data) {
        if (item.key === key) {
            return item;
        }

        if (item.children) {
            const found = findItemByKey(item.children, key);
            if (found) {
                return found;
            }
        }
    }
    return null;
};

export const transformFoldersData = (
    apiData: IFoldersAPIResponse | undefined,
    searchTerm?: string,
    isTrending?: boolean,
) => {
    if (!apiData) {
        return { data: [], totalCount: 0, totalTopLevelCount: 0 };
    }

    return {
        data: transformWithSearch(apiData.Data, searchTerm, undefined, isTrending),
        totalCount: apiData.TotalCount || 0,
        totalTopLevelCount: apiData.TotalTopLevelCount || 0,
    };
};
